import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Pencil, Power, ArrowLeft } from 'lucide-react';
import { hasEditAccess } from '../permissionConfig';
import { notifyEditBlocked } from '../components/EditAccessToast';
import {
  BASE, ICON, S, DpoBeamBackground, EMBEDDED_CSS, SectionTitle, Dropdown, ClearFiltersButton, num,
} from './distributorPoShared';

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

/* ============================================================================
 * Distributor Master — "Distributors & Platforms" (distributor_master rows:
 * margin/location/region/platform per distributor). Own page/URL, reached
 * from the DistributorMaster.jsx home screen, matching the DistributorPO /
 * DistributorPOHistory two-page pattern.
 * ========================================================================== */

const emptyRowForm = {
  id: null, distributor_name: '', platform_name: '', region: '', state: '',
  location: '', margin_pct: '', remarks: '', is_active: true,
};

export default function DistributorMasterRows({ user }) {
  const navigate = useNavigate();
  const canEdit = hasEditAccess(user, 'dms', 'distributor_master');
  const userName = user?.display_name || user?.username || 'Unknown';

  const [distributors, setDistributors] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterDistributor, setFilterDistributor] = useState('');
  const [filterPlatform, setFilterPlatform] = useState('');
  const [filterRegion, setFilterRegion] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [platformOptions, setPlatformOptions] = useState([]);

  const [showForm, setShowForm] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [form, setForm] = useState(emptyRowForm);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState('');
  const toastTimer = useRef(null);

  function showToast(msg) {
    setToast(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(''), 4200);
  }

  useEffect(() => {
    fetch(`${BASE}/distributor-master?action=distributors`)
      .then(r => r.json()).then(d => setDistributors(Array.isArray(d) ? d : []))
      .catch(() => setDistributors([]));
  }, []);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ action: 'rows' });
      if (filterDistributor) params.set('distributor', filterDistributor);
      if (filterPlatform)    params.set('platform', filterPlatform);
      if (filterRegion)      params.set('region', filterRegion);
      if (filterStatus)      params.set('status', filterStatus);
      const res  = await fetch(`${BASE}/distributor-master?${params}`);
      const data = await res.json();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) { /* non-fatal */ }
    setLoading(false);
  }, [filterDistributor, filterPlatform, filterRegion, filterStatus]);

  useEffect(() => { loadRows(); }, [loadRows]);

  useEffect(() => {
    setFilterPlatform(''); setPlatformOptions([]);
    if (!filterDistributor) return;
    fetch(`${BASE}/distributor-master?action=platforms&distributor=${encodeURIComponent(filterDistributor)}`)
      .then(r => r.json()).then(d => setPlatformOptions(Array.isArray(d) ? d : []))
      .catch(() => setPlatformOptions([]));
  }, [filterDistributor]);

  function openNew() {
    if (!canEdit) return notifyEditBlocked();
    setForm(emptyRowForm);
    setIsEdit(false);
    setShowForm(true);
  }
  function openEdit(row) {
    if (!canEdit) return notifyEditBlocked();
    setForm({
      id: row.id, distributor_name: row.distributor_name || '', platform_name: row.platform_name || '',
      region: row.region || '', state: row.state || '', location: row.location || '',
      margin_pct: row.margin_pct ?? '', remarks: row.remarks || '', is_active: !!row.is_active,
    });
    setIsEdit(true);
    setShowForm(true);
  }
  async function toggleActive(row) {
    if (!canEdit) return notifyEditBlocked();
    const nextState = row.is_active ? 'deactivate' : 'reactivate';
    if (!window.confirm(`${nextState === 'deactivate' ? 'Deactivate' : 'Reactivate'} ${row.distributor_name} / ${row.platform_name}${row.region ? ' / ' + row.region : ''}?`)) return;
    try {
      const res  = await fetch(`${BASE}/distributor-master?action=update-row`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...row, is_active: !row.is_active, user_name: userName }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) return showToast(data.error || 'Could not update row');
      showToast(`${row.distributor_name} ${nextState}d`);
      loadRows();
    } catch (e) { showToast('Network error updating row'); }
  }

  async function handleSubmit() {
    if (!form.distributor_name.trim()) return showToast('Distributor name is required');
    if (!form.platform_name.trim())    return showToast('Platform is required');
    setSubmitting(true);
    try {
      const body = { ...form, margin_pct: form.margin_pct === '' ? null : num(form.margin_pct), user_name: userName };
      const res  = await fetch(`${BASE}/distributor-master?action=${isEdit ? 'update-row' : 'create-row'}`, {
        method: isEdit ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.success) return showToast(data.error || 'Could not save row');
      showToast(data.message);
      setShowForm(false);
      loadRows();
    } catch (e) { showToast('Network error saving row'); }
    setSubmitting(false);
  }

  return (
    <div className="dpo-page" style={S.page}>
      <style>{EMBEDDED_CSS}</style>

      <div style={S.headerBar}>
        <DpoBeamBackground />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, position: 'relative', zIndex: 1 }}>
          <button style={S.backBtn} onClick={() => navigate('/dms/distributor-master')}><ArrowLeft size={ICON.sm} />Back</button>
          <h1 style={{ ...S.h1 }}>Distributors <span style={{ fontWeight: 600, color: '#C9B7EA' }}>&amp; Platforms</span></h1>
        </div>
      </div>

      <div style={S.filterBox}>
        <div style={S.filterField}>
          <div style={S.statLabel}>Distributor</div>
          <Dropdown value={filterDistributor} onChange={setFilterDistributor}
            options={[{ value: '', label: 'All distributors' }, ...distributors.map(d => ({ value: d, label: d }))]} />
        </div>
        <div style={S.filterField}>
          <div style={S.statLabel}>Platform</div>
          <Dropdown value={filterPlatform} onChange={setFilterPlatform}
            options={[{ value: '', label: 'All platforms' }, ...platformOptions.map(p => ({ value: p, label: p }))]} />
        </div>
        <div style={S.filterField}>
          <div style={S.statLabel}>Region</div>
          <input style={S.input} value={filterRegion} placeholder="All regions" onChange={e => setFilterRegion(e.target.value)} />
        </div>
        <div style={S.filterField}>
          <div style={S.statLabel}>Status</div>
          <Dropdown value={filterStatus} onChange={setFilterStatus} options={STATUS_OPTIONS} />
        </div>
        {(filterDistributor || filterPlatform || filterRegion || filterStatus) && (
          <ClearFiltersButton onClear={() => { setFilterDistributor(''); setFilterPlatform(''); setFilterRegion(''); setFilterStatus(''); }} />
        )}
      </div>

      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <SectionTitle n={1} title="Distributors & Platforms" sub="Margin, region, and location per distributor + platform" />
          <button style={S.btnPrimary} onClick={openNew}><Plus size={ICON.sm} style={{ marginRight: 6, verticalAlign: -2 }} />Add New</button>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead>
              <tr>{['Distributor', 'Platform', 'Region', 'Location', 'State', 'Margin %', 'Status', 'Remarks', ''].map(h => <th key={h} style={S.th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} style={{ ...S.td, textAlign: 'center', color: 'var(--dpo-text-muted)' }}>Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={9} style={{ ...S.td, textAlign: 'center', color: 'var(--dpo-text-muted)' }}>No rows match these filters</td></tr>
              ) : rows.map(r => (
                <tr key={r.id}>
                  <td style={S.td}>{r.distributor_name}</td>
                  <td style={S.td}>{r.platform_name}</td>
                  <td style={S.td}>{r.region || '—'}</td>
                  <td style={S.td}>{r.location || '—'}</td>
                  <td style={S.td}>{r.state || '—'}</td>
                  <td style={S.td}>{r.margin_pct != null ? `${r.margin_pct}%` : '—'}</td>
                  <td style={S.td}>
                    <span style={{ ...S.statusBadge, background: r.is_active ? 'var(--dpo-green-tint)' : 'var(--dpo-pink-tint)', color: r.is_active ? 'var(--dpo-green)' : 'var(--dpo-error)' }}>
                      {r.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={S.td}>{r.remarks || '—'}</td>
                  <td style={S.td}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button style={S.smallActionBtn} onClick={() => openEdit(r)}><Pencil size={ICON.xs} /></button>
                      <button style={S.smallActionBtn} onClick={() => toggleActive(r)}><Power size={ICON.xs} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <datalist id="dm-distributors">
          {distributors.map(d => <option key={d} value={d} />)}
        </datalist>

        {showForm && (
          <div style={S.overlay} onClick={() => setShowForm(false)}>
            <div style={{ ...S.modal, maxWidth: 520 }} onClick={e => e.stopPropagation()}>
              <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--dpo-text)', marginBottom: 4 }}>{isEdit ? 'Edit Distributor Row' : 'Add Distributor Row'}</div>
              <div style={{ fontSize: 12.5, color: 'var(--dpo-text-muted)', marginBottom: 18 }}>One row per distributor + platform + region.</div>
              <div style={S.bentoGrid}>
                <FormField label="Distributor *"><input style={S.input} list="dm-distributors" value={form.distributor_name} onChange={e => setForm(f => ({ ...f, distributor_name: e.target.value }))} /></FormField>
                <FormField label="Platform *"><input style={S.input} value={form.platform_name} onChange={e => setForm(f => ({ ...f, platform_name: e.target.value }))} /></FormField>
                <FormField label="Region"><input style={S.input} value={form.region} onChange={e => setForm(f => ({ ...f, region: e.target.value }))} /></FormField>
                <FormField label="Margin %"><input style={S.input} type="number" value={form.margin_pct} onChange={e => setForm(f => ({ ...f, margin_pct: e.target.value }))} /></FormField>
                <FormField label="Location"><input style={S.input} value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} /></FormField>
                <FormField label="State"><input style={S.input} value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))} /></FormField>
              </div>
              <div style={{ marginTop: 12 }}>
                <div style={S.statLabel}>Remarks</div>
                <textarea style={{ ...S.input, height: 64, padding: 10, marginTop: 6 }} value={form.remarks} onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, fontSize: 13, color: 'var(--dpo-text)', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
                Active
              </label>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 22 }}>
                <button style={S.btnGhost} onClick={() => setShowForm(false)}>Cancel</button>
                <button style={{ ...S.btnPrimary, opacity: submitting ? 0.7 : 1 }} disabled={submitting} onClick={handleSubmit}>
                  {submitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Row'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {toast && <div style={S.toast}>{toast}</div>}
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div style={S.bentoCard}>
      <div style={S.statLabel}>{label}</div>
      {children}
    </div>
  );
}
